// The gate a signed-out visitor hits on an internal page (issue #755).
//
// It used to render the whole welcome page: a feature showcase, two optional
// text fields, and a sign-in that sent you to /welcome afterwards. So someone
// opening a shared link to a case was shown a marketing panel, asked for their
// company, and then landed somewhere else entirely — having to navigate back to
// the thing they clicked.
//
// This says what you were trying to reach, signs you in, and returns you to it.
// The visitor is often someone we do not know, so it also names what IS open to
// them rather than leaving a dead end.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SignInButton, SignUpButton } from '@clerk/clerk-react';
import { colors } from '../theme';

// What a signed-out visitor can actually use. Kept here rather than in the copy
// so a dead end is impossible: if a route leaves this list, so does the link.
const OPEN_TO_EVERYONE = [
  { to: '/helpdesk', label: 'Helpdesk', desc: 'Look up a service tag and find the right contact' },
  { to: '/', label: 'Device lookup', desc: 'Warranty, status and history for a unit' },
  { to: '/wave-configurator', label: 'Wave configurator', desc: 'Configure a product and get its part number' },
];

export default function SignInPrompt({ pageName }) {
  const location = useLocation();
  // Come back to the page they actually wanted, not to /welcome.
  const returnTo = `${location.pathname}${location.search}` || '/';

  return (
    <div className="ni-page">
      <div className="ni-shell ni-shell--narrow">
        <div
          className="ni-panel"
          style={{
            maxWidth: 460, margin: '3rem auto', padding: '1.75rem 1.5rem',
            display: 'flex', flexDirection: 'column', gap: '1rem', textAlign: 'center',
          }}
        >
          <img src="/north-invent-logo.svg" alt="North Invent" style={{ height: 22, opacity: 0.9, margin: '0 auto' }} />

          <div>
            <h1 style={{ margin: '0 0 0.35rem', fontSize: '1.15rem', color: colors.textStrong }}>
              Sign in to continue
            </h1>
            <p style={{ margin: 0, color: colors.textMuted, lineHeight: 1.55, fontSize: '0.9rem' }}>
              {pageName
                ? <>You need an account to open <strong style={{ color: colors.textBody }}>{pageName}</strong>. We&rsquo;ll bring you straight back.</>
                : <>You need an account to open this page. We&rsquo;ll bring you straight back.</>}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <SignInButton mode="modal" fallbackRedirectUrl={returnTo}>
              <button className="ni-button ni-button--primary">Sign in</button>
            </SignInButton>
            <SignUpButton mode="modal" fallbackRedirectUrl={returnTo}>
              <button className="ni-button ni-button--ghost">Create account</button>
            </SignUpButton>
          </div>

          <div style={{ borderTop: `1px solid ${colors.panelBorder}`, paddingTop: '0.9rem', textAlign: 'left' }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.78rem', color: colors.textMuted }}>
              No account? These are open to everyone:
            </p>
            {OPEN_TO_EVERYONE.map(item => (
              <Link
                key={item.to}
                to={item.to}
                style={{ display: 'block', textDecoration: 'none', padding: '0.35rem 0', color: colors.primary, fontSize: '0.85rem' }}
              >
                {item.label}
                <span style={{ display: 'block', color: colors.textMuted, fontSize: '0.75rem' }}>{item.desc}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
